/**
 * GradeDistributionChart Component
 *
 * Bar chart showing the count of active routes at each V-grade.
 * Helps gym owners identify grade gaps in their route catalog.
 */

import { useMemo, type CSSProperties } from 'react';
import { V_GRADES, type VGrade } from '@flash/shared';

/**
 * Props for GradeDistributionChart component
 */
interface GradeDistributionChartProps {
  /** Count of routes at each grade */
  distribution: Record<VGrade, number>;
}

/**
 * Get color for a grade based on difficulty
 * Uses a gradient from green (easy) to red (hard)
 */
const getGradeColor = (grade: VGrade): string => {
  const index = V_GRADES.indexOf(grade);
  const ratio = index / (V_GRADES.length - 1);

  // Gradient from green (#22c55e) through yellow (#eab308) to red (#ef4444)
  if (ratio < 0.5) {
    // Green to yellow
    const adjustedRatio = ratio * 2;
    const r = Math.round(34 + (234 - 34) * adjustedRatio);
    const g = Math.round(197 + (179 - 197) * adjustedRatio);
    const b = Math.round(94 + (8 - 94) * adjustedRatio);
    return `rgb(${r}, ${g}, ${b})`;
  } else {
    // Yellow to red
    const adjustedRatio = (ratio - 0.5) * 2;
    const r = Math.round(234 + (239 - 234) * adjustedRatio);
    const g = Math.round(179 + (68 - 179) * adjustedRatio);
    const b = Math.round(8 + (68 - 8) * adjustedRatio);
    return `rgb(${r}, ${g}, ${b})`;
  }
};

/**
 * GradeDistributionChart displays the distribution of routes across grades
 *
 * @example
 * ```tsx
 * <GradeDistributionChart distribution={analytics.gradeDistribution} />
 * ```
 */
export const GradeDistributionChart = ({ distribution }: GradeDistributionChartProps) => {
  // Calculate max count for scaling bars
  const maxCount = useMemo(() => {
    return Math.max(1, ...Object.values(distribution));
  }, [distribution]);

  // Calculate total routes
  const totalRoutes = useMemo(() => {
    return Object.values(distribution).reduce((sum, count) => sum + count, 0);
  }, [distribution]);

  // Find grades with routes for highlighting gaps
  const gradesWithRoutes = useMemo(() => {
    const grades: VGrade[] = [];
    V_GRADES.forEach((grade) => {
      if (distribution[grade] > 0) {
        grades.push(grade);
      }
    });
    return grades;
  }, [distribution]);

  // Determine display range (V0 to highest grade with routes + buffer)
  const displayGrades = useMemo(() => {
    if (gradesWithRoutes.length === 0) {
      // Show V0-V8 as default range
      return V_GRADES.slice(0, 9);
    }

    const maxGradeIdx = Math.max(...gradesWithRoutes.map((g) => V_GRADES.indexOf(g)));
    const displayEndIdx = Math.min(V_GRADES.length - 1, maxGradeIdx + 2); // +2 buffer
    return V_GRADES.slice(0, displayEndIdx + 1);
  }, [gradesWithRoutes]);

  if (totalRoutes === 0) {
    return (
      <div style={containerStyle}>
        <h3 style={titleStyle}>Grade Distribution</h3>
        <div style={emptyStateStyle}>
          <p style={emptyTextStyle}>No active routes.</p>
          <p style={emptySubtextStyle}>Routes will appear here once they are created.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <div>
          <h3 style={titleStyle}>Grade Distribution</h3>
          <p style={subtitleStyle}>Active routes by difficulty (V0-V17)</p>
        </div>
        <span style={totalStyle}>{totalRoutes} total routes</span>
      </div>

      <div style={chartContainerStyle}>
        {displayGrades.map((grade) => {
          const count = distribution[grade];
          const barWidth = (count / maxCount) * 100;
          const color = getGradeColor(grade);
          const isEmpty = count === 0;

          return (
            <div key={grade} style={barRowStyle}>
              <div style={gradeLabelStyle}>{grade}</div>
              <div style={barContainerStyle}>
                <div
                  style={{
                    ...barStyle,
                    width: `${barWidth}%`,
                    backgroundColor: isEmpty ? '#e5e7eb' : color,
                    minWidth: isEmpty ? '0' : '4px',
                  }}
                />
              </div>
              <div style={countLabelStyle}>
                {count}
                {isEmpty && <span style={gapIndicatorStyle}> (gap)</span>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div style={legendStyle}>
        <div style={legendItemStyle}>
          <div style={{ ...legendColorStyle, backgroundColor: '#22c55e' }} />
          <span style={legendTextStyle}>Beginner (V0-V3)</span>
        </div>
        <div style={legendItemStyle}>
          <div style={{ ...legendColorStyle, backgroundColor: '#eab308' }} />
          <span style={legendTextStyle}>Intermediate (V4-V8)</span>
        </div>
        <div style={legendItemStyle}>
          <div style={{ ...legendColorStyle, backgroundColor: '#ef4444' }} />
          <span style={legendTextStyle}>Advanced (V9+)</span>
        </div>
      </div>
    </div>
  );
};

/**
 * Component styles
 */
const containerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
};

const headerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  flexWrap: 'wrap',
  gap: '8px',
};

const titleStyle: CSSProperties = {
  margin: 0,
  fontSize: '1.125rem',
  fontWeight: 600,
  color: '#1f2937',
};

const subtitleStyle: CSSProperties = {
  margin: '4px 0 0 0',
  fontSize: '0.875rem',
  color: '#6b7280',
};

const totalStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: '#6b7280',
  fontWeight: 500,
};

const emptyStateStyle: CSSProperties = {
  padding: '32px',
  textAlign: 'center',
  backgroundColor: '#f9fafb',
  borderRadius: '8px',
  border: '1px dashed #d1d5db',
};

const emptyTextStyle: CSSProperties = {
  margin: '0 0 8px 0',
  fontSize: '0.875rem',
  color: '#374151',
  fontWeight: 500,
};

const emptySubtextStyle: CSSProperties = {
  margin: 0,
  fontSize: '0.8125rem',
  color: '#6b7280',
};

const chartContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
};

const barRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
};

const gradeLabelStyle: CSSProperties = {
  width: '36px',
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: '#374151',
  textAlign: 'right',
  flexShrink: 0,
};

const barContainerStyle: CSSProperties = {
  flex: 1,
  height: '24px',
  backgroundColor: '#f3f4f6',
  borderRadius: '4px',
  overflow: 'hidden',
};

const barStyle: CSSProperties = {
  height: '100%',
  borderRadius: '4px',
  transition: 'width 0.3s ease',
};

const countLabelStyle: CSSProperties = {
  width: '60px',
  fontSize: '0.8125rem',
  fontWeight: 500,
  color: '#374151',
  textAlign: 'left',
  flexShrink: 0,
};

const gapIndicatorStyle: CSSProperties = {
  color: '#ef4444',
  fontSize: '0.6875rem',
  fontWeight: 400,
};

const legendStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  flexWrap: 'wrap',
  gap: '16px',
  marginTop: '8px',
  paddingTop: '12px',
  borderTop: '1px solid #e5e7eb',
};

const legendItemStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
};

const legendColorStyle: CSSProperties = {
  width: '14px',
  height: '14px',
  borderRadius: '3px',
};

const legendTextStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: '#6b7280',
};

export default GradeDistributionChart;
